import { useMemo } from 'react'
import { StoragePlan } from 'types'

// Storage plan limits configuration
const STORAGE_PLAN_LIMITS = {
    [StoragePlan.free]: {
        products: 20,
        categories: 10,
        locations: 10,
        transactions: false,
        suppliers: false,
        customers: false,
        sharedAccess: false,
        customInputsPerCategory: 5,
        allowedCustomInputTypes: ['text', 'number'],
        aiProductSuggestionsPerDay: 0,
    },
    [StoragePlan.pro]: {
        products: 100,
        categories: 20,
        locations: 20,
        transactions: true,
        suppliers: true,
        customers: true,
        sharedAccess: true,
        customInputsPerCategory: 15,
        allowedCustomInputTypes: ['text', 'number', 'checkbox', 'textarea', 'select', 'radio'],
        aiProductSuggestionsPerDay: 10,
    },
    [StoragePlan.premium]: {
        products: 1000,
        categories: 50,
        locations: 50,
        transactions: true,
        suppliers: true,
        customers: true,
        sharedAccess: true,
        customInputsPerCategory: 40,
        allowedCustomInputTypes: ['text', 'number', 'checkbox', 'textarea', 'select', 'radio'],
        aiProductSuggestionsPerDay: 100,
    },
}

const MAX_STORAGES_PER_USER = 50

interface UseStorageLimitsProps {
    storagePlan?: StoragePlan
    currentStorageCount?: number
    currentProductCount?: number
    currentCategoryCount?: number
    currentLocationCount?: number
}

const useStorageLimits = ({
    storagePlan = StoragePlan.free,
    currentStorageCount = 0,
    currentProductCount = 0,
    currentCategoryCount = 0,
    currentLocationCount = 0,
}: UseStorageLimitsProps = {}) => {

    // Get limits for current storage plan
    const limits = useMemo(() => STORAGE_PLAN_LIMITS[storagePlan], [storagePlan])

    // Check if user can add more storages (global limit)
    const canAddStorage = useMemo(() => {
        return currentStorageCount < MAX_STORAGES_PER_USER
    }, [currentStorageCount])

    // Check if user can add more products (per storage)
    const canAddProduct = useMemo(() => {
        return currentProductCount < limits.products
    }, [currentProductCount, limits.products])

    // Check if user can add more categories (per storage)
    const canAddCategory = useMemo(() => {
        return currentCategoryCount < limits.categories
    }, [currentCategoryCount, limits.categories])

    const canAddLocation = useMemo(() => {
        return currentLocationCount < limits.locations
    }, [currentLocationCount, limits.locations])

    // Check if storage plan supports transactions
    const canUseTransactions = useMemo(() => {
        return limits.transactions
    }, [limits.transactions])

    // Check if storage plan supports suppliers
    const canUseSuppliers = useMemo(() => {
        return limits.suppliers
    }, [limits.suppliers])

    // Check if storage plan supports customers
    const canUseCustomers = useMemo(() => {
        return limits.customers
    }, [limits.customers])

    // Check if storage plan supports shared access
    const canUseSharedAccess = useMemo(() => {
        return limits.sharedAccess
    }, [limits.sharedAccess])

    const canUseAiProductSuggestions = useMemo(() => {
        return (limits.aiProductSuggestionsPerDay || 0) > 0
    }, [limits.aiProductSuggestionsPerDay])

    // Check plan type
    const isFreePlan = useMemo(() => {
        return storagePlan === StoragePlan.free
    }, [storagePlan])

    const isProPlan = useMemo(() => {
        return storagePlan === StoragePlan.pro
    }, [storagePlan])

    const isPremiumPlan = useMemo(() => {
        return storagePlan === StoragePlan.premium
    }, [storagePlan])

    return {
        storagePlan,
        limits,
        maxStorages: MAX_STORAGES_PER_USER,
        canAddStorage,
        canAddProduct,
        canAddCategory,
        canAddLocation,
        canUseTransactions,
        canUseSuppliers,
        canUseCustomers,
        canUseSharedAccess,
        canUseAiProductSuggestions,
        isFreePlan,
        isProPlan,
        isPremiumPlan,
    }
}

export default useStorageLimits
